import React, { useRef, useEffect } from 'react';

// 自适应调控模块共享主题 + 画布折线图 (与平台其余页一致的纯白冷灰风格)
export const TH = {
  bg: '#FFFFFF', canvasBg: '#FFFFFF', grid: 'rgba(30,42,58,0.08)',
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  rl: '#C2533A', base: '#2874A6', ok: '#1E8449', warn: '#C0392B',
  fontUI: '"Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};

export type Series = { x: number[]; y: number[]; color: string; label: string; dash?: boolean };

// 通用多序列折线图 (自适应 DPR, 网格 + 轴 + 图例)
export function LineChart({ series, title, xlabel, ylabel, height = 200, yRef }:
  { series: Series[]; title?: string; xlabel?: string; ylabel?: string; height?: number; yRef?: { v: number; label: string } }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    const W = cv.clientWidth, H = cv.clientHeight;
    cv.width = W * dpr; cv.height = H * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    ctx.font = `11px ${TH.fontMono}`;
    const mL = 46, mR = 12, mT = title ? 22 : 10, mB = 26;
    const pw = W - mL - mR, ph = H - mT - mB;
    const all = series.flatMap((s) => s.y).concat(yRef ? [yRef.v] : []);
    const xs = series.flatMap((s) => s.x);
    if (!all.length || !xs.length) return;
    let ymin = Math.min(...all), ymax = Math.max(...all);
    if (ymin === ymax) { ymax += 1; ymin -= 1; }
    ymin = Math.min(ymin, 0); ymax += (ymax - ymin) * 0.08;
    const xmin = Math.min(...xs), xmax = Math.max(...xs) || 1;
    const px = (x: number) => mL + ((x - xmin) / (xmax - xmin || 1)) * pw;
    const py = (y: number) => mT + ph - ((y - ymin) / (ymax - ymin || 1)) * ph;
    // 网格 + y 刻度
    ctx.strokeStyle = TH.grid; ctx.fillStyle = TH.textMuted; ctx.lineWidth = 1;
    ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
    for (let i = 0; i <= 4; i++) {
      const yv = ymin + (ymax - ymin) * i / 4, y = py(yv);
      ctx.beginPath(); ctx.moveTo(mL, y); ctx.lineTo(W - mR, y); ctx.stroke();
      ctx.fillText(yv.toFixed(yv >= 100 ? 0 : 1), mL - 5, y);
    }
    // x 刻度
    ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    for (let i = 0; i <= 4; i++) {
      const xv = xmin + (xmax - xmin) * i / 4;
      ctx.fillText(xv.toFixed(0), px(xv), mT + ph + 5);
    }
    // 参考线 (如砂堵临界)
    if (yRef) {
      ctx.strokeStyle = TH.warn; ctx.setLineDash([5, 4]); ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(mL, py(yRef.v)); ctx.lineTo(W - mR, py(yRef.v)); ctx.stroke();
      ctx.setLineDash([]); ctx.fillStyle = TH.warn; ctx.textAlign = 'left';
      ctx.fillText(yRef.label, mL + 4, py(yRef.v) - 8);
    }
    // 曲线
    series.forEach((s) => {
      ctx.strokeStyle = s.color; ctx.lineWidth = 1.8;
      if (s.dash) ctx.setLineDash([6, 4]); else ctx.setLineDash([]);
      ctx.beginPath();
      s.x.forEach((x, i) => { const X = px(x), Y = py(s.y[i]); i ? ctx.lineTo(X, Y) : ctx.moveTo(X, Y); });
      ctx.stroke(); ctx.setLineDash([]);
    });
    // 标题 + 图例
    if (title) { ctx.fillStyle = TH.text; ctx.font = `600 12px ${TH.fontUI}`; ctx.textAlign = 'left'; ctx.textBaseline = 'top'; ctx.fillText(title, mL, 4); }
    ctx.font = `11px ${TH.fontUI}`; ctx.textBaseline = 'middle';
    let lx = W - mR;
    [...series].reverse().forEach((s) => {
      const tw = ctx.measureText(s.label).width; lx -= tw + 18;
      ctx.strokeStyle = s.color; ctx.lineWidth = 2; ctx.setLineDash(s.dash ? [4, 3] : []);
      ctx.beginPath(); ctx.moveTo(lx, mT + 2); ctx.lineTo(lx + 12, mT + 2); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle = TH.textSec; ctx.textAlign = 'left'; ctx.fillText(s.label, lx + 15, mT + 2);
    });
    if (ylabel) { ctx.save(); ctx.translate(11, mT + ph / 2); ctx.rotate(-Math.PI / 2); ctx.fillStyle = TH.textMuted; ctx.textAlign = 'center'; ctx.fillText(ylabel, 0, 0); ctx.restore(); }
    if (xlabel) { ctx.fillStyle = TH.textMuted; ctx.textAlign = 'center'; ctx.fillText(xlabel, mL + pw / 2, H - 11); }
  });
  return <canvas ref={ref} style={{ width: '100%', height, display: 'block' }} />;
}

// 缝网轮廓对比 (侧视: 长度 × 缝高/2 椭圆)
export function FracOutline({ rl, base, height = 200 }: { rl: number[][]; base: number[][]; height?: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    const W = cv.clientWidth, H = cv.clientHeight;
    cv.width = W * dpr; cv.height = H * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const pts = [...rl, ...base]; if (!pts.length) return;
    const maxX = Math.max(...pts.map((p) => Math.abs(p[0]))) || 1;
    const maxY = Math.max(...pts.map((p) => Math.abs(p[1]))) || 1;
    const cx = W / 2, cy = H / 2;
    const sx = (W / 2 - 20) / maxX, sy = (H / 2 - 20) / maxY;
    const draw = (o: number[][], color: string) => {
      ctx.strokeStyle = color; ctx.lineWidth = 1.8; ctx.beginPath();
      o.forEach((p, i) => { const X = cx + p[0] * sx, Y = cy - p[1] * sy; i ? ctx.lineTo(X, Y) : ctx.moveTo(X, Y); });
      // 镜像下半
      for (let i = o.length - 1; i >= 0; i--) { const p = o[i]; ctx.lineTo(cx + p[0] * sx, cy + p[1] * sy); }
      ctx.closePath(); ctx.stroke();
      ctx.fillStyle = color + '22'; ctx.fill();
    };
    ctx.strokeStyle = TH.grid; ctx.beginPath(); ctx.moveTo(cx, 8); ctx.lineTo(cx, H - 8); ctx.stroke(); // 井筒
    draw(base, TH.base); draw(rl, TH.rl);
    ctx.font = `11px ${TH.fontMono}`; ctx.fillStyle = TH.textMuted; ctx.textAlign = 'center';
    ctx.fillText(`半缝长 ±${maxX.toFixed(0)}m`, cx, H - 6);
  });
  return <canvas ref={ref} style={{ width: '100%', height, display: 'block' }} />;
}

export const PANEL: React.CSSProperties = { background: TH.canvasBg, border: `1px solid ${TH.headerBorder}`, borderRadius: 4 };
export const PANEL_HDR: React.CSSProperties = { padding: '6px 12px', background: TH.panelHeaderBg, borderBottom: `1px solid ${TH.headerBorder}`, fontSize: 12, fontWeight: 600, color: TH.text };
